import { useEffect, useRef, useState } from 'react';
import { useSearch } from '@tanstack/react-router';
import {
  usePreviewBulkReprocess,
  useCreateBulkReprocessRun,
  useListAdminOrganizations,
  useListAdminInterviewTypes,
  useListAdminInterviewTypeVersions,
  useGetMe,
  CreateBulkReprocessRunDtoMode,
  type BulkReprocessFilterDto,
} from '@sara/api-client';
import type { BulkReprocessSearch } from '../../../../router/tree';
import { readLastOrgSlug } from '../../../../lib/last-org';
import { takeReplaySessionIds } from '../../../../lib/replay-handoff';
import { cn } from '@sara/ui/lib/utils';
import { Button } from '@sara/ui/components/button';
import { Input } from '@sara/ui/components/input';
import { Textarea } from '@sara/ui/components/textarea';
import { Switch } from '@sara/ui/components/switch';
import { Separator } from '@sara/ui/components/separator';
import { Tabs, TabsList, TabsTrigger } from '@sara/ui/components/tabs';
import { RadioGroup, RadioGroupItem } from '@sara/ui/components/radio-group';
import { Field, FieldContent, FieldLabel } from '@sara/ui/components/field';
import { toast } from '@sara/ui/components/sonner';
import { getApiErrorMessage } from '../../../../lib/api-error';
import { MODE_METADATA } from './display';
import { CatalogCombobox } from './CatalogCombobox';
import { BulkReprocessPreviewTable } from './BulkReprocessPreviewTable';

interface BulkReprocessLaunchFormProps {
  onCreated: (runId: string) => void;
}

const PAGE_SIZE = 15;

type IdFilterMode = 'external' | 'session';

const MODE_OPTIONS = Object.values(CreateBulkReprocessRunDtoMode).map((value) => ({
  value,
  ...MODE_METADATA[value],
}));

interface FilterFormState {
  idFilterMode: IdFilterMode;
  externalUserIdsText: string;
  sessionIdsText: string;
  orgId: string;
  interviewTypeId: string;
  interviewTypeVersionId: string;
  createdAfter: string;
  createdBefore: string;
  completedNoResults: boolean;
  /** Sessions the operator removed from the preview — subtracted from the run. */
  excludedIds: string[];
}

/** Split on newlines or commas, trim, drop empties. */
function parseList(text: string): string[] {
  return text
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

// The date inputs are plain `YYYY-MM-DD`, but prefill params from the sessions
// list are full ISO datetimes — trim to the date portion.
function toDateInputValue(iso: string | undefined): string {
  return iso ? iso.slice(0, 10) : '';
}

/**
 * Seeds the form from the sessions list's "Replay Completed Sessions"
 * hand-off (InterviewListContainerContent). A carried-over org/date/version
 * filter always means external-IDs mode.
 */
function buildInitialState(prefill: Partial<BulkReprocessSearch>): FilterFormState {
  return {
    idFilterMode: 'external',
    externalUserIdsText: prefill.externalUserId ?? '',
    sessionIdsText: '',
    orgId: prefill.orgId ?? '',
    interviewTypeId: '',
    interviewTypeVersionId: prefill.interviewTypeVersionId ?? '',
    createdAfter: toDateInputValue(prefill.createdAfter),
    createdBefore: toDateInputValue(prefill.createdBefore),
    // Default ON in external mode: without an explicit session-id list, the
    // common intent is to backfill sessions that finished but never produced
    // results. The hand-off can still override it.
    completedNoResults: prefill.completedNoResults ?? true,
    excludedIds: [],
  };
}

function buildFilter(state: FilterFormState): BulkReprocessFilterDto {
  const excludeSessionIds = state.excludedIds.length > 0 ? state.excludedIds : undefined;
  if (state.idFilterMode === 'session') {
    const sessionIds = parseList(state.sessionIdsText);
    return { sessionIds: sessionIds.length > 0 ? sessionIds : undefined, excludeSessionIds };
  }
  const externalUserIds = parseList(state.externalUserIdsText);
  return {
    orgId: state.orgId || undefined,
    interviewTypeVersionId: state.interviewTypeVersionId || undefined,
    externalUserIds: externalUserIds.length > 0 ? externalUserIds : undefined,
    createdAfter: state.createdAfter ? new Date(state.createdAfter).toISOString() : undefined,
    createdBefore: state.createdBefore
      ? new Date(`${state.createdBefore}T23:59:59.999Z`).toISOString()
      : undefined,
    completedNoResults: state.completedNoResults || undefined,
    excludeSessionIds,
  };
}

export function BulkReprocessLaunchForm({ onCreated }: BulkReprocessLaunchFormProps) {
  // Cross-cutting: this form is only mounted under `/admin/bulk-reprocess`, but
  // that route object isn't exported for import here — same loose-mode pattern
  // as `BulkReprocessContainer`/`useOrgSlug`.
  const prefill = useSearch({ strict: false }) as Partial<BulkReprocessSearch>;
  const [state, setState] = useState<FilterFormState>(() => buildInitialState(prefill));
  const [mode, setMode] = useState<CreateBulkReprocessRunDtoMode>(
    CreateBulkReprocessRunDtoMode.FAILED
  );
  const [clientError, setClientError] = useState<string | null>(null);
  const [datesTouched, setDatesTouched] = useState(false);
  const [sessionIdsTouched, setSessionIdsTouched] = useState(false);

  const isSessionMode = state.idFilterMode === 'session';

  const { data: orgs } = useListAdminOrganizations();
  const { data: me } = useGetMe();
  const { data: templates, isFetching: templatesFetching } = useListAdminInterviewTypes(
    state.orgId,
    { query: { enabled: state.orgId.length > 0 } }
  );

  // Resolve the Template picker from a prefilled version id (the hand-off carries a
  // version, not a template) by matching `latestVersionId` once the org's templates
  // load — derived during render instead of committed into state via an effect.
  const resolvedTemplateId =
    !state.interviewTypeId && state.interviewTypeVersionId
      ? (templates?.find((t) => t.latestVersionId === state.interviewTypeVersionId)?.id ?? '')
      : '';
  const effectiveInterviewTypeId = state.interviewTypeId || resolvedTemplateId;

  const { data: versions, isFetching: versionsFetching } = useListAdminInterviewTypeVersions(
    effectiveInterviewTypeId,
    { query: { enabled: effectiveInterviewTypeId.length > 0 } }
  );

  const {
    mutate: preview,
    data: previewResult,
    isPending: isPreviewing,
    error: previewError,
    reset: resetPreview,
  } = usePreviewBulkReprocess();

  const {
    mutate: createRun,
    isPending: isCreating,
    error: createError,
  } = useCreateBulkReprocessRun({
    mutation: {
      onSuccess: (data) => {
        toast.success('Bulk replay run started', {
          description: `${data.totalCount} session${data.totalCount === 1 ? '' : 's'} queued.`,
        });
        onCreated(data.runId);
      },
      onError: (err) => {
        toast.error('Could not start the run', { description: getApiErrorMessage(err) });
      },
    },
  });

  // Sessions-list "Replay Completed Sessions" hand-off: the resolved session ids
  // arrive via sessionStorage (consume-once). Load them into Session-IDs mode
  // and auto-preview so the operator lands on the exact matching set.
  const handoffRef = useRef(false);
  useEffect(() => {
    if (handoffRef.current || prefill.handoff !== 'sessions') return;
    handoffRef.current = true;
    const ids = takeReplaySessionIds();
    if (!ids || ids.length === 0) return;
    // oxlint-disable-next-line react/set-state-in-effect -- consume-once sessionStorage handoff; can't be a lazy `useState` initializer since StrictMode double-invokes those, which would drop the ids on the throwaway call.
    setState((prev) => ({ ...prev, idFilterMode: 'session', sessionIdsText: ids.join('\n') }));
    preview({ data: { sessionIds: ids }, params: { page: 1, pageSize: PAGE_SIZE } });
  }, [prefill.handoff, preview]);

  // Default the Organization picker to the org the operator was last in (unless
  // the hand-off already prefilled one). Seeds once, when `me` loads — clearing
  // the org afterward won't re-trigger it.
  const orgSeededRef = useRef(prefill.orgId !== undefined);
  useEffect(() => {
    if (orgSeededRef.current || state.orgId || !me) return;
    const lastSlug = readLastOrgSlug();
    const lastOrgId = lastSlug
      ? me.memberships.find((m) => m.organization.slug === lastSlug)?.organization.id
      : undefined;
    // oxlint-disable-next-line react/set-state-in-effect -- one-time seed guarded by `orgSeededRef`; `orgId` feeds the submitted filter (via `buildFilter`), so it must become real committed state here, not just a derived display value.
    if (lastOrgId) setState((prev) => ({ ...prev, orgId: lastOrgId }));
    orgSeededRef.current = true;
  }, [me, state.orgId]);

  const filter = buildFilter(state);

  /** Any filter edit clears the current preview (re-locking Launch) + errors.
   *  Removals are relative to a specific preview, so drop them too — a changed
   *  filter is a fresh selection. */
  function invalidatePreview() {
    resetPreview();
    setClientError(null);
    setState((prev) => (prev.excludedIds.length ? { ...prev, excludedIds: [] } : prev));
  }

  function update<K extends keyof FilterFormState>(key: K, value: FilterFormState[K]) {
    setState((prev) => ({ ...prev, [key]: value }));
    invalidatePreview();
  }

  function selectIdFilterMode(next: IdFilterMode) {
    if (next === state.idFilterMode) return;
    setState((prev) =>
      next === 'session'
        ? // Session IDs are a complete target set on their own — clear the org/
          // template/date filters that no longer apply (and are hidden).
          {
            ...prev,
            idFilterMode: 'session',
            orgId: '',
            interviewTypeId: '',
            interviewTypeVersionId: '',
            createdAfter: '',
            createdBefore: '',
            completedNoResults: false,
          }
        : // Back to external mode — restore the default "completed, no results"
          // constraint (see buildInitialState).
          { ...prev, idFilterMode: 'external', completedNoResults: true }
    );
    setDatesTouched(false);
    setSessionIdsTouched(false);
    invalidatePreview();
  }

  function selectOrg(orgId: string) {
    // Org drives the template list — changing it invalidates template + version.
    setState((prev) => ({ ...prev, orgId, interviewTypeId: '', interviewTypeVersionId: '' }));
    invalidatePreview();
  }

  function templateLatestVersionId(interviewTypeId: string): string {
    return templates?.find((t) => t.id === interviewTypeId)?.latestVersionId ?? '';
  }

  function selectTemplate(interviewTypeId: string) {
    // Default the version to the template's current/latest.
    setState((prev) => ({
      ...prev,
      interviewTypeId,
      interviewTypeVersionId: templateLatestVersionId(interviewTypeId),
    }));
    invalidatePreview();
  }

  function selectVersion(versionId: string) {
    // The backend filters by version only (Template is a version-narrowing UI),
    // so a template with an empty version would silently apply no constraint.
    // Deselecting therefore falls back to the template's latest, never empty.
    const nextVersionId = versionId || templateLatestVersionId(effectiveInterviewTypeId);
    setState((prev) => ({
      // Commit the prefill-resolved template id (see `resolvedTemplateId` above): once the
      // operator picks a version, `resolvedTemplateId`'s own latest-version match can stop
      // holding (a non-latest pick no longer matches any template), so the id must become
      // real state now rather than staying a per-render derivation.
      ...prev,
      interviewTypeId: prev.interviewTypeId || effectiveInterviewTypeId,
      interviewTypeVersionId: nextVersionId,
    }));
    invalidatePreview();
  }

  function validate(): string | null {
    if (isSessionMode) {
      if (parseList(state.sessionIdsText).length === 0) return 'Session IDs are required.';
    } else if (!state.createdAfter || !state.createdBefore) {
      return 'Created after and Created before are required.';
    }
    return null;
  }

  function handlePreview() {
    const err = validate();
    if (err) {
      setClientError(err);
      if (isSessionMode) setSessionIdsTouched(true);
      else setDatesTouched(true);
      return;
    }
    setClientError(null);
    preview({ data: filter, params: { page: 1, pageSize: PAGE_SIZE } });
  }

  function handlePageChange(page: number) {
    preview({ data: filter, params: { page, pageSize: PAGE_SIZE } });
  }

  // Drop a session from the previewed set so it won't be processed. Re-previews
  // with the updated exclusion list (the count reflects the removal), stepping
  // back a page if we just removed the last row on the final page.
  function handleRemove(sessionId: string) {
    const nextState = { ...state, excludedIds: [...state.excludedIds, sessionId] };
    setState(nextState);
    const currentPage = previewResult?.page ?? 1;
    const remaining = Math.max(0, (previewResult?.count ?? 1) - 1);
    const totalPages = Math.max(1, Math.ceil(remaining / PAGE_SIZE));
    const nextPage = Math.min(currentPage, totalPages);
    preview({ data: buildFilter(nextState), params: { page: nextPage, pageSize: PAGE_SIZE } });
  }

  function handleConfirm() {
    createRun({ data: { mode, filter } });
  }

  const busy = isPreviewing || isCreating;
  // Nothing to launch once every previewed session has been removed.
  const launchDisabled = !previewResult || previewResult.count === 0 || busy;
  const datesInvalid = datesTouched && (!state.createdAfter || !state.createdBefore);
  const sessionIdsInvalid = sessionIdsTouched && parseList(state.sessionIdsText).length === 0;

  const orgOptions = (orgs ?? []).map((o) => ({ value: o.id, label: o.name }));
  const templateOptions = (templates ?? []).map((t) => ({
    value: t.id,
    label: t.name,
    hint: t.archived ? 'archived' : undefined,
  }));
  const versionOptions = (versions ?? []).map((v) => ({
    value: v.id,
    label: `v${v.version}`,
    hint: v.isLatest ? 'current' : undefined,
  }));

  return (
    <section className="space-y-6 rounded-xl border border-border bg-card p-5">
      <header className="space-y-1">
        <h2 className="text-base font-semibold text-foreground">Launch a run</h2>
        <p className="text-[13px] text-muted-foreground">
          Filter by explicit session IDs, or by organization / template / date filters
          (AND-combined). Dates are required unless filtering by session IDs.
        </p>
      </header>

      {/* ID filter mode — the first choice; determines what else is shown. */}
      <div className="space-y-2">
        <Tabs
          value={state.idFilterMode}
          onValueChange={(v) => selectIdFilterMode(v as IdFilterMode)}
        >
          <TabsList>
            <TabsTrigger value="external" disabled={busy}>
              External user IDs
            </TabsTrigger>
            <TabsTrigger value="session" disabled={busy}>
              Session IDs
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <p className="text-[12.5px] text-muted-foreground">
          {isSessionMode ? (
            <>
              Target an explicit set of sessions by ID.
              <span className="font-semibold text-destructive"> (required)</span>
            </>
          ) : (
            'Optionally narrow to specific external user IDs, combined with the filters below.'
          )}
        </p>
        <Textarea
          aria-label={isSessionMode ? 'Session IDs' : 'External user IDs'}
          placeholder={
            isSessionMode
              ? 'One session UUID per line (or comma-separated)…'
              : 'One external user ID per line (or comma-separated)…'
          }
          rows={4}
          value={isSessionMode ? state.sessionIdsText : state.externalUserIdsText}
          onChange={(e) =>
            update(isSessionMode ? 'sessionIdsText' : 'externalUserIdsText', e.target.value)
          }
          disabled={busy}
          className={cn(sessionIdsInvalid && 'border-destructive')}
        />
      </div>

      {/* Hidden entirely in Session IDs mode — an explicit list is a complete
          target set, so org/template/date filters are irrelevant. */}
      {!isSessionMode && (
        <div className="space-y-4">
          <Field>
            <FieldLabel htmlFor="bulk-reprocess-org">Organization</FieldLabel>
            <FieldContent className="max-w-[460px]">
              <CatalogCombobox
                id="bulk-reprocess-org"
                value={state.orgId}
                options={orgOptions}
                onChange={selectOrg}
                placeholder="All organizations"
                searchPlaceholder="Search organizations…"
                emptyText="No organizations found."
                disabled={busy}
              />
            </FieldContent>
          </Field>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field className="sm:col-span-2">
              <FieldLabel htmlFor="bulk-reprocess-template">Template</FieldLabel>
              <FieldContent>
                <CatalogCombobox
                  id="bulk-reprocess-template"
                  value={effectiveInterviewTypeId}
                  options={templateOptions}
                  onChange={selectTemplate}
                  placeholder="All templates"
                  disabledPlaceholder="Select an organization first"
                  searchPlaceholder="Search templates…"
                  emptyText="No templates for this organization."
                  disabled={busy || !state.orgId}
                  loading={templatesFetching}
                />
              </FieldContent>
            </Field>
            <Field>
              <FieldLabel htmlFor="bulk-reprocess-version">Template version</FieldLabel>
              <FieldContent>
                <CatalogCombobox
                  id="bulk-reprocess-version"
                  value={state.interviewTypeVersionId}
                  options={versionOptions}
                  onChange={selectVersion}
                  placeholder="Latest"
                  disabledPlaceholder="Select a template first"
                  searchPlaceholder="Search versions…"
                  emptyText="No versions."
                  disabled={busy || !effectiveInterviewTypeId}
                  loading={versionsFetching}
                />
              </FieldContent>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="bulk-reprocess-createdAfter">
                Created after <span className="text-destructive">*</span>
              </FieldLabel>
              <FieldContent>
                <Input
                  id="bulk-reprocess-createdAfter"
                  type="date"
                  value={state.createdAfter}
                  onChange={(e) => update('createdAfter', e.target.value)}
                  disabled={busy}
                  className={cn(datesInvalid && !state.createdAfter && 'border-destructive')}
                />
              </FieldContent>
            </Field>
            <Field>
              <FieldLabel htmlFor="bulk-reprocess-createdBefore">
                Created before <span className="text-destructive">*</span>
              </FieldLabel>
              <FieldContent>
                <Input
                  id="bulk-reprocess-createdBefore"
                  type="date"
                  value={state.createdBefore}
                  onChange={(e) => update('createdBefore', e.target.value)}
                  disabled={busy}
                  className={cn(datesInvalid && !state.createdBefore && 'border-destructive')}
                />
              </FieldContent>
            </Field>
          </div>

          <Field orientation="horizontal" className="items-center justify-between">
            <FieldLabel
              htmlFor="bulk-reprocess-completedNoResults"
              className="flex-col items-start gap-0.5"
            >
              <span className="text-sm font-medium text-foreground">
                Process only Completed Sessions without results stored
              </span>
              <span className="text-[12.5px] font-normal text-muted-foreground">
                Sessions that finished the call but never produced extraction/rubric results.
              </span>
            </FieldLabel>
            <Switch
              id="bulk-reprocess-completedNoResults"
              checked={state.completedNoResults}
              onCheckedChange={(checked) => update('completedNoResults', checked)}
              disabled={busy}
            />
          </Field>
        </div>
      )}

      <Separator />

      {/* Mode — always visible regardless of ID-filter mode. */}
      <RadioGroup
        value={mode}
        onValueChange={(v) => setMode(v as CreateBulkReprocessRunDtoMode)}
        className="gap-2.5"
      >
        {MODE_OPTIONS.map((option) => {
          const selected = option.value === mode;
          return (
            <FieldLabel
              key={option.value}
              htmlFor={`bulk-reprocess-mode-${option.value}`}
              className={cn(
                'flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors',
                selected ? 'border-primary bg-primary/8' : 'border-border hover:bg-accent/50'
              )}
            >
              <RadioGroupItem
                id={`bulk-reprocess-mode-${option.value}`}
                value={option.value}
                className="sr-only"
              />
              <span
                className={cn(
                  'flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
                  selected ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'
                )}
              >
                <option.Icon className="h-4 w-4" strokeWidth={1.7} />
              </span>
              <span className="space-y-0.5">
                <span className="block text-sm font-semibold text-foreground">
                  {option.cardTitle}
                </span>
                <span className="block max-w-[560px] text-[12.5px] font-normal text-muted-foreground">
                  {option.cardDescription}
                </span>
              </span>
            </FieldLabel>
          );
        })}
      </RadioGroup>

      {/* Preview → Launch */}
      <div className="flex flex-wrap items-center gap-3 border-t border-border/70 pt-4">
        <Button type="button" variant="outline" size="sm" onClick={handlePreview} disabled={busy}>
          {isPreviewing ? 'Previewing…' : 'Preview'}
        </Button>
        <Button
          type="button"
          variant="tinted"
          size="sm"
          onClick={handleConfirm}
          disabled={launchDisabled}
        >
          {isCreating ? 'Launching…' : 'Launch run'}
        </Button>

        {clientError || previewError ? (
          <span className="text-sm text-destructive">
            {clientError ?? getApiErrorMessage(previewError) ?? 'Preview failed'}
          </span>
        ) : previewResult ? (
          <span className="text-sm text-foreground">
            <strong className="tabular-nums">{previewResult.count}</strong>{' '}
            {previewResult.count === 1
              ? 'session matches these filters.'
              : 'sessions match these filters.'}
            {state.excludedIds.length > 0 && (
              <span className="ml-1 text-muted-foreground">
                ({state.excludedIds.length} removed)
              </span>
            )}
          </span>
        ) : (
          <span className="text-[12.5px] text-muted-foreground">
            Run Preview to verify before launching.
          </span>
        )}
      </div>

      {previewResult && (
        <BulkReprocessPreviewTable
          count={previewResult.count}
          page={previewResult.page}
          pageSize={previewResult.pageSize}
          items={previewResult.items}
          onPageChange={handlePageChange}
          isFetching={isPreviewing}
          onRemove={handleRemove}
        />
      )}

      {createError ? (
        <p className="text-sm text-destructive">
          {getApiErrorMessage(createError) ?? 'Failed to start the run'}
        </p>
      ) : null}
    </section>
  );
}
